import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '@/i18n';
import { reportClientError } from '@/shared/lib/reportError';
import { CHUNK_ERROR_RX, RELOAD_GUARD_KEY } from '@/shared/lib/lazyRetry';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Error boundary global. Captura cualquier fallo de render en el árbol
 * y muestra una pantalla amigable con opción de recargar, en lugar de
 * dejar el <div id="root"> vacío.
 *
 * Nota: cubre errores de render y ciclo de vida de React. NO cubre errores
 * dentro de handlers async (esos hay que capturarlos con try/catch), ni
 * de otros arboles de React. Para eso RouterProvider tiene su propio
 * errorElement.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Registrar para debugging futuro
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info);
    // Persistir en client_errors → aviso inmediato por email.
    reportClientError({
      source: 'boundary',
      level: 'fatal',
      message: error.message,
      stack: error.stack ?? null,
      context: { componentStack: info.componentStack?.slice(0, 2000) },
    });
    try {
      // Guardar el mensaje para diagnóstico posterior
      localStorage.setItem(
        '__last_error',
        JSON.stringify({
          message: error.message,
          stack: error.stack,
          when: new Date().toISOString(),
        })
      );
    } catch {
      /* ignore */
    }
  }

  private handleReload = () => {
    // Limpiar la flag anti-loop del chunk error para poder reintentar
    try {
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  private handleHardReset = async () => {
    try {
      // Desregistrar service workers
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // Vaciar todas las cachés
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      /* ignore */
    }
    // Limpiar sessionStorage anti-loop
    try {
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    // Recarga forzada
    window.location.href = '/';
  };

  override render() {
    if (this.state.error) {
      const isChunkError = CHUNK_ERROR_RX.test(
        this.state.error.message
      );

      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            background: '#F7F9FF',
            color: '#0F172A',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 380 }}>
            <div
              style={{
                width: 56,
                height: 56,
                margin: '0 auto 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 14,
                background: '#002DA0',
                color: '#fff',
                fontSize: 28,
              }}
            >
              ÷
            </div>
            <h1
              style={{
                fontSize: 22,
                marginBottom: 8,
                fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
                color: '#002DA0',
              }}
            >
              {i18n.t('common.error_boundary_title')}
            </h1>
            <p style={{ fontSize: 14, color: '#64748B', marginBottom: 20 }}>
              {isChunkError
                ? i18n.t('common.error_boundary_chunk_body')
                : i18n.t('common.error_boundary_generic_body')}
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: '#002DA0',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: 10,
              }}
            >
              {i18n.t('common.error_boundary_reload')}
            </button>
            <button
              type="button"
              onClick={this.handleHardReset}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: 'transparent',
                color: '#64748B',
                border: '1px solid #DDE2F5',
                borderRadius: 4,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {i18n.t('common.error_boundary_hard_reset')}
            </button>
            <details style={{ marginTop: 20, textAlign: 'left' }}>
              <summary
                style={{
                  fontSize: 12,
                  color: '#6B6B80',
                  cursor: 'pointer',
                }}
              >
                {i18n.t('common.error_boundary_details_summary')}
              </summary>
              <pre
                style={{
                  marginTop: 8,
                  fontSize: 11,
                  color: '#8B2C2C',
                  background: '#FBEDED',
                  padding: 8,
                  borderRadius: 4,
                  overflow: 'auto',
                  maxHeight: 200,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {(this.state.error?.stack ?? this.state.error?.message ?? String(this.state.error))}
              </pre>
            </details>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
